"use strict";
'use client';
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DropdownContent = exports.DropdownTrigger = exports.DropdownMenu = exports.DropdownContext = void 0;
const react_1 = __importStar(require("react"));
const utils_1 = require("../utils");
exports.DropdownContext = (0, react_1.createContext)(undefined);
const useDropdown = () => {
    const context = (0, react_1.useContext)(exports.DropdownContext);
    if (!context) {
        throw new Error('useDropdown must be used within a DropdownMenu');
    }
    return context;
};
// DropdownMenu Component
const DropdownMenu = ({ children, className, initialOpen = false, closeOnItemClick = true, controlledOpen, onClose }) => {
    const [isOpen, setIsOpen] = (0, react_1.useState)(initialOpen);
    const menuRef = (0, react_1.useRef)(null);
    // Controlled or uncontrolled state
    const currentIsOpen = controlledOpen !== undefined ? controlledOpen : isOpen;
    const toggleDropdown = () => setIsOpen((prev) => !prev);
    const closeDropdown = () => {
        setIsOpen(false);
        onClose?.();
    };
    const handleOutsideClick = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
            closeDropdown();
        }
    };
    (0, react_1.useEffect)(() => {
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);
    const contextValue = react_1.default.useMemo(() => ({
        isOpen: currentIsOpen,
        toggleDropdown,
        closeDropdown,
        closeOnItemClick,
    }), [currentIsOpen, closeOnItemClick]);
    return (react_1.default.createElement(exports.DropdownContext.Provider, { value: contextValue },
        react_1.default.createElement("div", { ref: menuRef, className: (0, utils_1.cn)("relative inline-block", className) }, children)));
};
exports.DropdownMenu = DropdownMenu;
// DropdownTrigger Component
const DropdownTrigger = ({ children, asChild, className, activeClassName }) => {
    const { toggleDropdown, isOpen } = useDropdown();
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleDropdown();
        }
    };
    if (asChild && react_1.default.isValidElement(children)) {
        return react_1.default.cloneElement(children, {
            onClick: (e) => {
                children.props.onClick?.(e);
                toggleDropdown();
            },
            onKeyDown: (e) => {
                children.props.onKeyDown?.(e);
                handleKeyDown(e);
            },
            'aria-haspopup': 'menu',
            'aria-expanded': isOpen,
            className: (0, utils_1.cn)(children.props.className, className, isOpen && activeClassName),
        });
    }
    return (react_1.default.createElement("button", { onClick: toggleDropdown, onKeyDown: handleKeyDown, "aria-haspopup": "menu", "aria-expanded": isOpen, className: (0, utils_1.cn)("outline-none", className, isOpen && activeClassName) }, children));
};
exports.DropdownTrigger = DropdownTrigger;
// DropdownContent Component
const DropdownContent = ({ children, className }) => {
    const { isOpen, closeDropdown, closeOnItemClick } = useDropdown();
    const [shouldRender, setShouldRender] = (0, react_1.useState)(false);
    const [isAnimating, setIsAnimating] = (0, react_1.useState)(false);
    (0, react_1.useEffect)(() => {
        if (isOpen) {
            setShouldRender(true);
            const animationTimeout = setTimeout(() => {
                setIsAnimating(true);
            }, 10);
            return () => clearTimeout(animationTimeout);
        }
        else {
            setIsAnimating(false);
            const removeTimeout = setTimeout(() => {
                setShouldRender(false);
            }, 300);
            return () => clearTimeout(removeTimeout);
        }
    }, [isOpen]);
    if (!shouldRender)
        return null;
    const handleItemClick = (event) => {
        if (closeOnItemClick) {
            closeDropdown();
        }
        else {
            event.stopPropagation(); // Prevent dropdown from closing
        }
    };
    return (react_1.default.createElement("div", { className: (0, utils_1.cn)("absolute left-0 mt-2 min-w-[279px] p-3 bg-brandWhite rounded-lg shadow-lg z-[999] transform transition-all duration-300 ease-in-out", isAnimating ? "opacity-100 scale-100" : "opacity-0 scale-95", className), role: "menu", tabIndex: -1 }, react_1.default.Children.map(children, (child) => {
        if (react_1.default.isValidElement(child)) {
            return react_1.default.cloneElement(child, {
                onClick: (e) => {
                    child.props.onClick?.(e);
                    handleItemClick(e);
                },
            });
        }
        return child;
    })));
};
exports.DropdownContent = DropdownContent;
